import * as Yup from 'yup';
import { format, addDays, isBefore, startOfDay, isEqual, isAfter } from 'date-fns';
import React, { JSX, useState } from 'react';
// import { FormCtx } from '../../Form/types';
import { ElinxInfo, Contact, ElinxType } from './types';
import styles from './ElinxPages.module.scss';
import SelectOptionLocked from '@/components/UI/Select/SelectOptionLocked';
import { EmailField } from '@/components/UI';
import { useI18nLabels } from '@/utils/useI18nLabels';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/providers/Permissions.provider';
import { allCardBrands } from 'vopay';
import { useDialog } from '@/providers/Dialog.provider';
import AddEmailDialog from './AddEmailDialog/AddEmailDialog';

export type BasicOptions = {
  label: string;
  value: string;
  isVisible?: boolean;
};

import {
  Chip,
  CardCheckbox,
  Tooltip,
  Box,
  Select,
  TextField,
  GoogleAddressField,
  MultiSelect,
  DatepickerSimple,
  Radio,
  InteracWordmark,
  UploadFile,
  MaterialSymbol,
  capitalizeFirstLetterOfWords,
  SelectOption,
  sanitizeToNumeric,
  Timestamp,
  PaymentMethod,
  ElinxNotificationMethod,
  PaymentMethodType,
  withStyles,
  FieldData,
  FormCtx,
  NameOfFrequency,
} from 'vopay';
import { Button } from 'UI';

const classes = withStyles(styles);

export function useElinxBasicDetailsRows(
  contacts: { id: string; name: string; currency?: string }[],
  onContactSearch: (val: string) => void,
  showMore: boolean,
  formType: ElinxType,
  country: string,
  usdEftEnabled: boolean,
  contactBalance?: number,
): FieldData<ElinxInfo>[] {
  const [isNewContact, setIsNewContact] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.BasicDetails' });
  const { frequencyLabelMapper } = useI18nLabels();
  const { permissions } = usePermissions();
  const frequencyOptionValues: NameOfFrequency[] = [
    'daily',
    'weekly',
    'bi-weekly',
    'monthly',
    'semi-monthly',
    'quarterly',
    'semi-annually',
    'annually',
    'weekdays',
  ];
  const FrequencyOptions = frequencyOptionValues.map((value) => ({
    label: frequencyLabelMapper(value) || capitalizeFirstLetterOfWords(value),
    value,
  }));

  const CurrencyOptions: SelectOption<BasicOptions>[] = [
    { label: 'CAD', value: 'CAD', isVisible: country === 'CA' },
    { label: 'USD', value: 'USD', isVisible: country !== 'CA' || (country === 'CA' && usdEftEnabled) },
  ];
  const ScheduleTypeOptions: SelectOption<BasicOptions>[] = [
    { label: t('ScheduleType.Options.single'), value: 'single' },
    { label: t('ScheduleType.Options.recurring'), value: 'recurring' },
  ];

  const EndingOptions: SelectOption<BasicOptions>[] = [
    { label: t('Ending.Options.endDate'), value: 'End Date' },
    { label: t('Ending.Options.payments'), value: 'Payments' },
  ];

  const SemiMonthlyDateOptions: SelectOption<BasicOptions>[] = [
    { label: t('SemiMonthlyFrequencyType.Options.semiMonthFirstAndFifteenth'), value: 'SemiMonthFirstAndFifteenth' },
    { label: t('SemiMonthlyFrequencyType.Options.semiMonthFifteenthAndLast'), value: 'SemiMonthFifteenthAndLast' },
  ];

  const now = format(new Date(), 'yyyy-MM-dd');

  return [
    {
      label: () => t('Contact.label'),
      fieldName: 'Contact',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: ElinxInfo) => (values.Contact === 'new' ? undefined : values.Contact),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e) => {
            if (e.target.value !== 'new') {
              ctx.setFieldValue('PaymentMethodDisplayOptions', '');
            }
          },
        });
        return (
          <Select
            version="2"
            filterable
            onFilter={(e) => {
              e.preventDefault();
              e.debounce(() => {
                if (!e.filter || e.filter.length >= 3) {
                  onContactSearch(e.filter);
                }
              }, 1000);
            }}
            options={contacts
              .filter(
                (contact) =>
                  !!contact.name &&
                  contact.name.split(' ').filter((contactFilter) => !!contactFilter).length > 1 &&
                  (formType !== 'Pay' ||
                    !ctx.values.Currency ||
                    !contact.currency ||
                    contact.currency === ctx.values.Currency),
              )
              .slice(0, 99)}
            optionValue={(o) => o.id}
            renderSelectedValue={(data, { Placeholder }) => {
              if (isNewContact) return 'New Contact';

              if (!data) return <Placeholder />;

              return data.name;
            }}
            renderOption={(o, { Option, SelectedIcon, selected, preventDefault, disabled }) => (
              <Option
                onClick={() => {
                  if (disabled) {
                    preventDefault();
                  } else {
                    setIsNewContact(false);
                    ctx.setFieldValue('ExistingContactName', o.name);
                  }
                }}
              >
                {o.name}
                {selected && <SelectedIcon />}
              </Option>
            )}
            renderDropdownFooter={({ close }) => (
              <Box flx="col">
                <Button
                  iconLeft="add"
                  size="small"
                  onClick={() => {
                    setIsNewContact(true);
                    ctx.setFieldValue('OverrideEmailAddress', '');
                    ctx.setFieldValue('ExistingContactName', '');
                    ctx.setFieldValue('Contact', 'new');
                    ctx.setFieldValue('PaymentMethodDisplayOptions', 'New');
                    close();
                  }}
                >
                  {t('Contact.dropdownButton', { defaultValue: 'Add New Contact' })}
                </Button>
              </Box>
            )}
            placeholder={t('Contact.placeholder')}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Currency.label'),
      fieldName: 'Currency',
      getInputState: () => 'required',
      showField: () => formType !== 'Connect',
      serializeField: (values: ElinxInfo) => values.Currency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('Currency.placeholder')}
            options={CurrencyOptions.filter((option) => option.isVisible)}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Amount.label'),
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => formType !== 'Connect',
      serializeField: (values: ElinxInfo) => sanitizeToNumeric(values.Amount),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField variant="outlineShadow" preset="currency" placeholder={t('Amount.placeholder')} {...fieldData} />
        );
      },

      validate: () =>
        Yup.string()
          .test('amount-validation', t('Amount.Validation.greaterThanZero'), (value) => {
            return Number(sanitizeToNumeric(value)) > 0.0;
          })
          .test('balance-validation', t('Amount.Validation.exceedsBalance'), (value) => {
            if (formType !== 'Pay' || contactBalance === undefined) return true;
            return Number(sanitizeToNumeric(value)) <= contactBalance;
          })
          .required(),
    },
    {
      label: () => t('ScheduleType.label'),
      fieldName: 'Frequency',
      getInputState: () => (formType === 'Flex' ? 'disabled' : 'required'),
      showField: () => !!permissions.CreateScheduledTransactions && formType !== 'Connect' && formType !== 'Flex',
      serializeField: (values: ElinxInfo) => {
        if (!permissions.CreateScheduledTransactions) return undefined;
        if (isToday(values.ScheduleStartDate) && values.Frequency === 'single') {
          return undefined;
        }

        return values.Frequency;
      },
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            if (
              e.target.value === 'recurring' &&
              ctx.values.ScheduleStartDate &&
              isToday(ctx.values.ScheduleStartDate)
            ) {
              const selectedDate = new Date(Number(ctx.values.ScheduleStartDate));
              const newDate = addDays(selectedDate, 1).getTime();
              ctx.setFieldValue('ScheduleStartDate', newDate.toString());
            }
          },
        });

        return (
          <Select
            version="2"
            placeholder={t('ScheduleType.placeholder')}
            options={ScheduleTypeOptions}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: (ctx: FormCtx<ElinxInfo>) =>
        ctx.values.Frequency === 'recurring'
          ? t('ScheduleStartDate.Label.recurring')
          : formType === 'Pay' || formType === 'Flex'
            ? t('ScheduleStartDate.Label.requestedTransaction')
            : t('ScheduleStartDate.Label.collection'),
      fieldName: 'ScheduleStartDate',
      getInputState: () => 'required',
      showField: () => formType !== 'Connect' && formType !== 'Flex',
      serializeField: (values: ElinxInfo) => {
        if (!permissions.CreateScheduledTransactions) return undefined;
        const startDate = new Date(Number(values.ScheduleStartDate));
        if (isToday(values.ScheduleStartDate) && values.Frequency === 'single') {
          return undefined;
        }

        return format(startDate, 'yyyy-MM-dd');
      },

      tooltip: (ctx) =>
        ctx.values.Frequency !== 'recurring' ? (
          <Tooltip information={t('ScheduleStartDate.tooltip')} position="top" width="394px" />
        ) : undefined,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const selectedDate = new Date(Number(e.target.value));
            const endDate = new Date(Number(ctx.values.ScheduleEndDate));
            if (
              isAfter(selectedDate, endDate) ||
              format(selectedDate, 'yyyy-MM-dd') === format(endDate, 'yyyy-MM-dd')
            ) {
              const newDate = addDays(selectedDate, 1).getTime();
              ctx.setFieldValue('ScheduleEndDate', newDate.toString());
            }
          },
        });
        return (
          <DatepickerSimple
            variant="outlineShadow"
            isDisabled={fieldData.disabled || !permissions.CreateScheduledTransactions}
            min={new Date().getTime() as Timestamp}
            iconPosition="left"
            {...fieldData}
          />
        );
      },
      validate: () =>
        Yup.string()
          .required()
          .test(
            'start-date-validation',
            t('ScheduleStartDate.Validation.invalidDate', { now }),
            (value: string | undefined) => {
              const dateValue = value ? new Date(Number(value)) : undefined;
              return !dateValue || format(dateValue, 'yyyy-MM-dd') >= now;
            },
          ),
    },
    {
      label: () => t('Frequency.label'),
      fieldName: 'NameOfFrequency',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => values.NameOfFrequency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('Frequency.placeholder')}
            options={FrequencyOptions}
            sort={false}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    {
      label: () => t('SemiMonthlyFrequencyType.label'),
      fieldName: 'SemiMonthlyFrequencyType',
      showField: (values: ElinxInfo) => values.NameOfFrequency === 'semi-monthly',
      getInputState: () => 'required',
      serializeField: (values: ElinxInfo) => values.SemiMonthlyFrequencyType,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('SemiMonthlyFrequencyType.placeholder')}
            options={SemiMonthlyDateOptions}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    {
      label: () => t('Ending.label'),
      fieldName: 'Ending',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => values.Ending,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" placeholder={t('Ending.placeholder')} options={EndingOptions} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('EndingAfterPayments.label'),
      fieldName: 'EndingAfterPayments',
      getInputState: () => 'required',

      showField: (values: ElinxInfo) => values.Ending === 'Payments' && values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => sanitizeToNumeric(values.EndingAfterPayments),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="numericWholePositive"
            placeholder={t('EndingAfterPayments.placeholder')}
            {...fieldData}
          />
        );
      },
      validate: () =>
        Yup.number()
          .transform((value) => Number(value) || 0)
          .min(1, t('EndingAfterPayments.Validation.atLeastOne'))
          .required(),
    },
    {
      label: () => t('ScheduleEndDate.label'),
      fieldName: 'ScheduleEndDate',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Ending === 'End Date' && values.Frequency === 'recurring',
      serializeField: (values: ElinxInfo) => {
        const endDate = new Date(Number(values.ScheduleEndDate));
        return format(endDate, 'yyyy-MM-dd');
      },
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <DatepickerSimple
            variant="outlineShadow"
            min={addDays(new Date(Number(ctx.values.ScheduleStartDate)), 1).getTime() as Timestamp}
            iconPosition="left"
            placeholder=""
            {...fieldData}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: (ctx: FormCtx<ElinxInfo>) =>
        Yup.string()
          .required()
          .test('end-date-validation', t('ScheduleEndDate.Validation.invalidDate'), (value: string | undefined) => {
            const endDate = value ? new Date(Number(value)) : undefined;
            const startDate = new Date(Number(ctx.values.ScheduleStartDate));
            if (!endDate) return false;
            return !isEqual(endDate, startDate) && !isBefore(endDate, startDate);
          }),
    },
    {
      label: () => t('EmailAddress.label'),
      fieldName: 'OverrideEmailAddress',
      getInputState: () => 'required',
      showField: (values) => values.Contact !== 'new',
      serializeField: (values: ElinxInfo) => values.OverrideEmailAddress,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});

        return <TextField variant="outlineShadow" placeholder={t('EmailAddress.placeholder')} {...fieldData} />;
      },
      validate: (ctx) => (ctx.values.Contact !== 'new' ? Yup.string().required() : Yup.string()),
    },
    {
      label: () => t('TransactionLabel.label'),
      fieldName: 'TransactionLabel',
      getInputState: () => 'optional',
      showField: () => showMore,
      serializeField: (values: ElinxInfo) => values.TransactionLabel,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
    },
    {
      label: () => t('Note.label'),
      fieldName: 'Note',
      getInputState: (ctx) => (ctx.values.Frequency === 'recurring' ? 'required' : 'optional'),
      showField: (values: ElinxInfo) => (values.Frequency === 'recurring' ? true : showMore),
      serializeField: (values: ElinxInfo) => values.Note,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: (ctx) => (ctx.values.Frequency === 'recurring' ? Yup.string().required() : Yup.string()),
    },
  ];
}

export function useElinxRowsContactDetails(): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ContactDetails' });

  const ContactTypeOptions: SelectOption<BasicOptions>[] = [
    { label: t('ContactType.Options.individual', { defaultValue: 'Individual' }), value: 'individual' },
    { label: t('ContactType.Options.business', { defaultValue: 'Business' }), value: 'business' },
  ];

  return [
    {
      label: () => t('ContactType.label', { defaultValue: 'Contact Type' }),
      fieldName: 'ContactType',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.ContactType,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" options={ContactTypeOptions} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('FirstName.label'),
      fieldName: 'FirstName',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new' && values.ContactType === 'individual',
      serializeField: (values: ElinxInfo) => values.FirstName?.replace(/, /g, ' ').replace(/,/g, ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('FirstName.placeholder')} {...fieldData} />;
      },

      validate: () => Yup.string().required(),
    },
    {
      label: () => t('LastName.label'),
      fieldName: 'LastName',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new' && values.ContactType === 'individual',
      serializeField: (values: ElinxInfo) => values.LastName?.replace(/, /g, ' ').replace(/,/g, ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('LastName.placeholder')} {...fieldData} />;
      },

      validate: () => Yup.string().required(),
    },
    {
      label: () => t('BusinessName.label', { defaultValue: 'Business Name' }),
      fieldName: 'BusinessName',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new' && values.ContactType === 'business',
      serializeField: (values: ElinxInfo) => values.BusinessName?.replace(/, /g, ' ').replace(/,/g, ''),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            placeholder={t('BusinessName.placeholder', { defaultValue: 'Enter business name' })}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    {
      label: () => t('EmailAddress.label'),
      fieldName: 'NewEmailAddress',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.NewEmailAddress,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="email"
            placeholder={t('EmailAddress.placeholder')}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
  ];
}

export function useElinxContactAddressRows(Country: string): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ContactAddress' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global.Countries' });

  const CountryOptions = [
    { label: tGlobal('CA'), value: 'CA' },
    { label: tGlobal('USLong'), value: 'US' },
  ].filter((o) => o.value === Country);

  function getRequiredInputState() {
    return Country === 'CA' ? 'optional' : 'required';
  }

  return [
    {
      label: () => t('Address.label'),
      fieldName: 'AddressLine1',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.AddressLine1,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <GoogleAddressField
            variant="outlineShadow"
            ISOCountry={[Country]}
            onGoogleAddressSelect={(addressComponents) => {
              ctx.setFieldValue('AddressLine1', addressComponents.address);
              ctx.setFieldValue('City', addressComponents.city);
              ctx.setFieldValue('Province', addressComponents.province);
              ctx.setFieldValue('PostalCode', addressComponents.postalcode);
              ctx.setFieldValue('Country', addressComponents.country);
              ctx.setFieldTouched('AddressLine1', true);
              ctx.setFieldTouched('City', true);
              ctx.setFieldTouched('Province', true);
              ctx.setFieldTouched('PostalCode', true);
              ctx.setFieldTouched('Country', true);
            }}
            placeholder={t('Address.placeholder')}
            {...fieldData}
          />
        );
      },
      validate: () => (Country === 'CA' ? Yup.string() : Yup.string().required()),
    },
    {
      label: () => t('Unit.label'),
      fieldName: 'AddressLine2',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.AddressLine2,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('Unit.placeholder')} {...fieldData} />;
      },
    },
    {
      label: () => t('City.label'),
      fieldName: 'City',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.City,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('City.placeholder')} {...fieldData} />;
      },
      validate: () => (Country === 'CA' ? Yup.string() : Yup.string().required()),
    },
    {
      label: () => t('Province.label'),
      fieldName: 'Province',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.Province,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('Province.placeholder')} {...fieldData} />;
      },
      validate: () => (Country === 'CA' ? Yup.string() : Yup.string().required()),
    },
    {
      label: () => t('Country.label'),
      fieldName: 'Country',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.Country,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <Select version="2" placeholder={t('Country.placeholder')} options={CountryOptions} {...fieldData} />;
      },
      validate: () => (Country === 'CA' ? Yup.string() : Yup.string().required()),
    },
    {
      label: () => t('PostalCode.label'),
      fieldName: 'PostalCode',
      getInputState: getRequiredInputState,
      showField: (values: ElinxInfo) => values.Contact === 'new',
      serializeField: (values: ElinxInfo) => values.PostalCode,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('PostalCode.placeholder')} {...fieldData} />;
      },
      validate: () => (Country === 'CA' ? Yup.string() : Yup.string().required()),
    },
  ];
}

export function useElinxPaymentSettingsRows(
  formType: ElinxType,
  country: string,
  contactName: string,
  paymentMethods: PaymentMethod[],
): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ContactPaymentSettings' });

  const AccountSelectionMethodOptions = [
    { label: t('AccountSelectionMethod.Options.manual'), value: 'manual' },
    { label: t('AccountSelectionMethod.Options.online'), value: 'online' },
  ];

  type PaymentSelectionMethodOption = {
    label: string;
    value: string;
    featureKey: string;
    visible: boolean;
    renderLabel?: JSX.Element;
  };

  const PaymentSelectionMethodOptions: PaymentSelectionMethodOption[] = [
    {
      label: t('RequestPaymentMethodType.Options.bankAccount'),
      value: 'bank',
      featureKey: 'Bank Account',
      visible: true,
    },
    {
      label: t('RequestPaymentMethodType.Options.interacETransfer'),
      renderLabel: <InteracWordmark type={formType === 'Pay' ? 'eTransfer' : 'moneyRequest'} />,
      value: 'email',
      featureKey: formType === 'Pay' ? 'Interac e-Transfer' : 'Interac e-Transfer Money Request',
      visible: country === 'CA' && formType !== 'Flex' && formType !== 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.email'),
      value: 'email',
      featureKey: 'Email',
      visible: country === 'CA' && formType === 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.creditCard'),
      value: 'credit',
      featureKey: 'Credit Card',
      visible: formType !== 'Pay',
    },
    {
      label: t('RequestPaymentMethodType.Options.debitCard'),
      value: 'debitcard',
      featureKey: 'Debit Card',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.googlePay'),
      value: 'googlePay',
      featureKey: 'Google Pay',
      visible: formType === 'Collect' || formType === 'Flex',
    },
    {
      label: t('RequestPaymentMethodType.Options.paypal'),
      value: 'paypal',
      featureKey: 'PayPal',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      label: t('RequestPaymentMethodType.Options.venmo'),
      value: 'venmo',
      featureKey: 'Venmo',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
    {
      label: t('RequestPaymentMethodType.Options.zelle', { defaultValue: 'Zelle' }),
      value: 'zelle',
      featureKey: 'Zelle',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
  ];

  type PaymentMethodOption = { label: string; value: string; isComingSoon: boolean };
  const paymentMethodsOptions: PaymentMethodOption[] = [
    {
      label: t('PaymentMethodOptions.Options.new', { contactName }),
      value: 'New',
      isComingSoon: false,
    },
    {
      label: t('PaymentMethodOptions.Options.existing', { contactName }),
      value: 'Existing',
      isComingSoon: true,
    },
  ];

  type PaymentMethodMultiOption = { label: string; value: string; disabled: boolean };

  // type VerificationMethodOption = {
  //   label: string;
  //   value: string;
  //   featureKey: string;
  //   disabled: boolean;
  //   tooltip?: React.ReactNode;
  // };

  return [
    {
      label: () => t('PaymentMethodOptions.label'),
      fieldName: 'PaymentMethodDisplayOptions',
      getInputState: () => (contactName === '' ? 'disabled' : 'required'),
      showField: () => formType !== 'Connect',
      serializeField: (values: ElinxInfo) => {
        const displayOptions = values.PaymentMethodDisplayOptions;
        return displayOptions !== 'Existing' && displayOptions !== 'New' ? 'Both' : displayOptions;
      },
      tooltip: () => (
        <Tooltip
          information={
            <Box gapSize="14px" flx="col">
              <Box gapSize="4px" flx="col">
                <h3 className={classes('toolTipHeader')}>{t('PaymentMethodOptions.Tooltip.newPaymentMethodTitle')}</h3>
                <div>{t('PaymentMethodOptions.Tooltip.newPaymentMethodDescription')}</div>
              </Box>
              <Box gapSize="4px" flx="col">
                <h3 className={classes('toolTipHeader')}>
                  {t('PaymentMethodOptions.Tooltip.existingPaymentMethodTitle')}
                </h3>
                <div>{t('PaymentMethodOptions.Tooltip.existingPaymentMethodDescription')}</div>
              </Box>
            </Box>
          }
          position="top"
          width="394px"
        />
      ),
      renderField: ({ getFormikData, ctx }) => {
        const selectFieldData = getFormikData<string>({});
        const multiSelectFieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });
        const filteredPaymentMethods = paymentMethods.filter(
          (paymentMethod) =>
            ctx.values.PaymentSelectionMethod?.includes(mapToPaymentSelectionValue(paymentMethod.PaymentMethod)) ||
            !ctx.values.PaymentSelectionMethod ||
            ctx.values.PaymentSelectionMethod === '',
        );
        const paymentMethodsMuiltiOptions: PaymentMethodMultiOption[] = [
          {
            label: t('PaymentMethodOptions.Options.new', { contactName }),
            value: 'New',
            disabled: false,
          },
          {
            label: t('PaymentMethodOptions.Options.existing', { contactName }),
            value: 'Existing',
            disabled: filteredPaymentMethods.length === 0,
          },
        ];

        return formType === 'Flex' ? (
          <Select
            version="2"
            placeholder={t('PaymentMethodOptions.placeholder')}
            options={paymentMethodsOptions}
            sort={false}
            renderOption={(o, { Option, SelectedIcon, selected, preventDefault }) => (
              <Option
                onClick={() => {
                  if (o.isComingSoon) {
                    preventDefault();
                  }
                }}
              >
                <Box flx="row" ai="center" jc="sb" width="100%">
                  <div className={classes(o.isComingSoon && 'optionDisabled')}> {o.label}</div>
                  {o.isComingSoon && <Chip label={t('PaymentMethodOptions.comingSoonChip')} />}

                  {selected && <SelectedIcon />}
                </Box>
              </Option>
            )}
            renderDropdownHeader={() => (
              <div className={classes('selectHeader')}>{t('PaymentMethodOptions.selectHeader')}</div>
            )}
            {...selectFieldData}
          />
        ) : (
          <MultiSelect
            placeholder={t('PaymentMethodOptions.placeholder')}
            disableOptionWhen={(option) => option.disabled}
            options={paymentMethodsMuiltiOptions}
            {...multiSelectFieldData}
          />
        );
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('ReceiverPhoneNumber.label'),
      fieldName: 'ReceiverPhoneNumber',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        (!!values.PaymentMethodDisplayOptions && values.PaymentMethodDisplayOptions !== 'New') ||
        (!!values.PaymentSelectionMethod && values.PaymentSelectionMethod.includes('bank') && country === 'US'),
      serializeField: (values: ElinxInfo) => values.ReceiverPhoneNumber,
      tooltip: () => (
        <Tooltip information={<div>{t('ReceiverPhoneNumber.tooltip')}</div>} position="top" width="298px" />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('RequestPaymentMethodType.label'),
      fieldName: 'PaymentSelectionMethod',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: ElinxInfo) =>
        values.PaymentSelectionMethod?.split(',')
          .filter((paymentMethod) => paymentMethod)
          .join(','),
      tooltip: () => (
        <Tooltip
          information={
            <Box gapSize="4px" flx="col">
              <h3 className={classes('toolTipHeader')}>{t('RequestPaymentMethodType.Tooltip.title')}</h3>
              <div>{t('RequestPaymentMethodType.Tooltip.description')}</div>
            </Box>
          }
          position="top"
          width="394px"
        />
      ),
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });

        // Filter payment methods based on existing payment methods when "Existing" is selected

        let paymentOptions =
          isToday(ctx.values.ScheduleStartDate) && ctx.values.Frequency === 'single'
            ? PaymentSelectionMethodOptions.filter((option) => option.visible)
            : PaymentSelectionMethodOptions.filter((option) => option.visible && option.value === 'bank');

        // If PaymentMethodDisplayOptions is "Existing", filter by available payment methods
        if (ctx.values.PaymentMethodDisplayOptions === 'Existing') {
          const existingPaymentMethodValues = new Set(
            paymentMethods.map((pm) => mapToPaymentSelectionValue(pm.PaymentMethod)),
          );
          paymentOptions = paymentOptions.filter((option) => existingPaymentMethodValues.has(option.value));
        }

        return (
          <MultiSelect
            placeholder={t('RequestPaymentMethodType.placeholder')}
            options={paymentOptions}
            renderOption={(option, args) => (
              <SelectOptionLocked {...args} featureKey={option.featureKey}>
                {option.renderLabel || option.label}
              </SelectOptionLocked>
            )}
            sort={false}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    // TODO: Uncomment when verification is needed
    // {
    //   label: () => t('Verify.label'),
    //   fieldName: 'Verify',
    //   getInputState: () => 'optional',
    //   showField: () => true,
    //   serializeField: (values: ElinxInfo) =>
    //     values.Verify?.split(',')
    //       .filter((verificationMethod) => verificationMethod)
    //       .join(','),

    //   renderField: ({ ctx, getFormikData }) => {
    //     const fieldData = getFormikData<string[], string>({
    //       formatValue: (value) => (value ? value.split(',') : []),
    //     });
    //     const PaymentVerificationMethodOptions: VerificationMethodOption[] = [
    //       {
    //         label: t('Verify.Options.bank'),
    //         value: 'bank',
    //         featureKey: 'Bank Account Verification',
    //         disabled:
    //           !ctx.values.PaymentSelectionMethod?.includes('bank') || ctx.values.AccountSelectionMethod === 'online',
    //       },
    //       {
    //         label: t('Verify.Options.interac'),
    //         value: 'interac',
    //         featureKey: 'Interac Verification',
    //         disabled: !ctx.values.PaymentSelectionMethod?.includes('email'),
    //       },
    //     ];
    //     return (
    //       <MultiSelect
    //         placeholder={t('Verify.placeholder')}
    //         options={PaymentVerificationMethodOptions}
    //         renderOption={(option, args) => (
    //           <SelectOptionLocked {...args} featureKey={option.featureKey} disabled={option.disabled}>
    //             {option.label}
    //           </SelectOptionLocked>
    //         )}
    //         sort={false}
    //         {...fieldData}
    //       />
    //     );
    //   },

    //   validate: () => Yup.mixed().notRequired(),
    // },
    {
      label: () => t('AccountSelectionMethod.label'),
      fieldName: 'AccountSelectionMethod',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => !!values.PaymentSelectionMethod?.includes('bank') && formType === 'Connect', // only show when bank account has been selected
      serializeField: (values: ElinxInfo) => {
        const accountValues = values.AccountSelectionMethod?.split(',');
        if (accountValues && accountValues.length > 1) {
          return 'any';
        }
        if (accountValues && accountValues.length === 1) {
          return accountValues[0];
        }
        return '';
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });
        return (
          <MultiSelect
            placeholder={t('AccountSelectionMethod.placeholder')}
            options={AccountSelectionMethodOptions}
            {...fieldData}
            // TODO: Uncomment when verification is needed
            // onChange={(e) => {

            //   if (e.target.value === 'online' && ctx.values.Verify?.includes('bank')) {
            //     const updatedVerify = ctx.values.Verify.split(',')
            //       .map((v) => v.trim())
            //       .filter((v) => v !== 'bank')
            //       .join(',');

            //     ctx.setFieldValue('Verify', updatedVerify);
            //   }
            //   fieldData.onChange(e);
            // }}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
    // move to the first container for now
    // {
    //   label: () => 'Email Address',
    //   fieldName: 'OverrideEmailAddress',
    //   getInputState: (ctx) => (ctx.values.SendEmail ? 'required' : 'optional'),
    //   showField: (values) => !!values.SendEmail && values.Contact !== 'new',
    //   serializeField: (values: ElinxInfo) => values.OverrideEmailAddress,
    //   renderField: ({ getFormikData }) => {
    //     const fieldData = getFormikData<string>({});

    //     return <TextField variant="outlineShadow" disabled={isReview} placeholder="Enter your name" {...fieldData} />;
    //   },
    //   validate: (ctx) => (ctx.values.SendEmail ? Yup.mixed().required('This value is required.') : Yup.mixed()),
    // },
  ];
}
export function useBankAccountRows(formType: ElinxType, country: string): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.BankAccount' });

  type BankingPaymentRailOption = {
    label: string;
    value: string;
    featureKey: string;
    visible: boolean;
    isRecurringEnabled: boolean;
  };

  const BankingPaymentRailOptions: BankingPaymentRailOption[] = [
    {
      label: t('BankingPaymentRail.Options.eft'),
      value: 'eft',
      featureKey: formType === 'Pay' ? 'EFTSendEnabled' : 'EFTCollectEnabled',
      visible: country === 'CA',
      isRecurringEnabled: true,
    },
    {
      label: 'VoPay Instant',
      value: 'vopayinstant',
      featureKey: 'RTRWithdrawEnabled',
      visible: country === 'CA' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      label: t('BankingPaymentRail.Options.ach'),
      value: 'ach',
      featureKey: country === 'US' && formType === 'Pay' ? 'ACHWithdrawEnabled' : 'ACHFundEnabled',
      visible: country === 'US',
      isRecurringEnabled: true,
    },
    {
      label: t('BankingPaymentRail.Options.rtp'),
      value: 'rtp',
      featureKey: 'RTPWithdrawEnabled',
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      label: t('BankingPaymentRail.Options.fedwire'),
      value: 'fedwire',
      featureKey: formType === 'Pay' ? 'FedwireWithdrawEnabled' : 'FedwireFundEnabled',
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
  ];

  const AccountSelectionMethodOptions = [
    { label: t('AccountSelectionMethod.Options.manual'), value: 'manual' },
    { label: t('AccountSelectionMethod.Options.online'), value: 'online' },
  ];

  return [
    {
      label: () => t('BankingPaymentRail.label'),
      fieldName: 'BankingPaymentRail',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => !!values.PaymentSelectionMethod?.includes('bank'),
      serializeField: (values: ElinxInfo) => values.BankingPaymentRail,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });

        // Filter based on visibility, schedule (today + single), and recurring capability
        const filteredOptions =
          isToday(ctx.values.ScheduleStartDate) && ctx.values.Frequency === 'single'
            ? BankingPaymentRailOptions.filter((option) => option.visible)
            : BankingPaymentRailOptions.filter((option) => option.visible && option.isRecurringEnabled);

        return (
          <MultiSelect
            placeholder={t('BankingPaymentRail.placeholder')}
            options={filteredOptions}
            renderOption={(option, args) => (
              <SelectOptionLocked {...args} featureKey={option.featureKey}>
                {option.label}
              </SelectOptionLocked>
            )}
            sort={false}
            {...fieldData}
          />
        );
      },
      validate: (ctx) =>
        ctx.values.PaymentSelectionMethod?.includes('bank') && formType !== 'Connect'
          ? Yup.string().required()
          : Yup.string(),
    },
    {
      label: () => t('AccountSelectionMethod.label'),
      fieldName: 'AccountSelectionMethod',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => !!values.PaymentSelectionMethod?.includes('bank'),
      serializeField: (values: ElinxInfo) => {
        const accountValues = values.AccountSelectionMethod?.split(',');
        if (accountValues && accountValues.length > 1) {
          return 'any';
        }
        if (accountValues && accountValues.length === 1) {
          return accountValues[0];
        }
        return '';
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',') : []),
        });
        return (
          <MultiSelect
            placeholder={t('AccountSelectionMethod.placeholder')}
            options={AccountSelectionMethodOptions}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
  ];
}

export function useCardSettingsRows(): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.CardSettings' });

  return [
    {
      label: () => t('acceptedCardBrands.label', { defaultValue: 'Accepted Card Brands' }),
      fieldName: 'AcceptedCardBrands',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => {
        const paymentMethods = values.PaymentSelectionMethod?.split(',') || [];
        return paymentMethods.includes('credit') || paymentMethods.includes('debitcard');
      },
      tooltip: () => (
        <Tooltip
          information={t('acceptedCardBrands.tooltip', {
            defaultValue: 'Select the card brands this contact should be able to provide.',
          })}
          position="top"
          width="394px"
        />
      ),
      serializeField: (values: ElinxInfo) => {
        const selectedBrands = values.AcceptedCardBrands?.split(',').filter((b) => b) || [];
        if (selectedBrands.length === allCardBrands.length) {
          return '';
        }
        return selectedBrands.join(',');
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string[], string>({
          formatValue: (value) => (value ? value.split(',').filter((b) => b) : []),
        });

        return (
          <MultiSelect
            placeholder={t('acceptedCardBrands.placeholder', { defaultValue: 'Select Card Brands' })}
            options={allCardBrands.map((brand) => ({
              label: brand.charAt(0).toUpperCase() + brand.slice(1),
              value: brand,
            }))}
            {...fieldData}
          />
        );
      },
      validate: (ctx) => {
        const paymentMethods = ctx.values.PaymentSelectionMethod?.split(',') || [];
        return paymentMethods.includes('credit') || paymentMethods.includes('debitcard')
          ? Yup.string().required()
          : Yup.string();
      },
    },
    {
      label: () => t('cardTypeValidation.label', { defaultValue: 'Card Type Validation' }),
      fieldName: 'CardTypeValidation',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) => {
        const paymentMethods = values.PaymentSelectionMethod?.split(',') || [];
        return paymentMethods.includes('credit') || paymentMethods.includes('debitcard');
      },
      tooltip: () => (
        <Tooltip
          information={t('cardTypeValidation.tooltip', {
            defaultValue:
              "Validates whether the entered card is debit or credit and blocks cards that don't match the allowed types.",
          })}
          position="top"
          width="394px"
        />
      ),
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<boolean>({
          formatValue: (value) => !!value,
        });
        return (
          <CardCheckbox
            label={t('cardTypeValidation.checkboxLabel', { defaultValue: 'Enable card type validation' })}
            checked={ctx.values.CardTypeValidation || false}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.boolean(),
    },
  ];
}

export function useElinxPartialPaymentRows(formType: ElinxType, isEnabled: boolean): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.PartialPayment' });
  const now = format(new Date(), 'yyyy-MM-dd');
  return [
    {
      label: () => t('MinimumPaymentAccepted.label'),
      fieldName: 'MinimumPaymentAccepted',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial'),
      serializeField: (values: ElinxInfo) =>
        formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial') && values.MinimumPaymentAccepted
          ? sanitizeToNumeric(String(values.MinimumPaymentAccepted))
          : undefined,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="currency"
            placeholder={t('MinimumPaymentAccepted.placeholder')}
            {...fieldData}
          />
        );
      },
      tooltip: () => <Tooltip information={t('MinimumPaymentAccepted.tooltip')} position="top" width="394px" />,
      validate: (ctx) =>
        Yup.string()
          .test('amount-validation', t('MinimumPaymentAccepted.Validation.greaterThanZero'), (value) => {
            const amountValidation = Number(sanitizeToNumeric(value)) <= 0.0;
            return !amountValidation;
          })
          .test(
            'amount-validation',
            t('MinimumPaymentAccepted.Validation.lessThanAmount', { amount: ctx.values.Amount }),
            (value) => {
              const amountValidation = Number(sanitizeToNumeric(value)) >= Number(sanitizeToNumeric(ctx.values.Amount));
              return !amountValidation;
            },
          )
          .required(),
    },
    {
      label: () => <span style={{ opacity: 0 }}>{t('ScheduleRemainingAmount.label')}</span>,
      fieldName: 'ScheduleRemainingAmount',
      getInputState: () => 'optional',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial'),
      serializeField: (values: ElinxInfo) =>
        formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial')
          ? values.ScheduleRemainingAmount
          : undefined,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});
        return (
          <CardCheckbox
            indicator="switch"
            checked={fieldData.value}
            label={
              <Box gapSize="4px">
                {t('ScheduleRemainingAmount.label')}{' '}
                <Tooltip position="top" information={t('ScheduleRemainingAmount.tooltip')} />
              </Box>
            }
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('RemainderPaymentDate.label'),
      fieldName: 'RemainderPaymentDate',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) => {
        return (
          !!values.ScheduleRemainingAmount &&
          isEnabled &&
          formType === 'Flex' &&
          !!values.FlexiblePaymentOptions?.includes('partial')
        );
      },
      serializeField: (values: ElinxInfo) => {
        if (!values.ScheduleRemainingAmount) {
          return undefined;
        }
        return formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('partial')
          ? format(new Date(Number(values.RemainderPaymentDate)), 'yyyy-MM-dd')
          : undefined;
      },
      tooltip: () => <Tooltip information={t('RemainderPaymentDate.tooltip')} position="top" width="394px" />,
      renderField: ({ ctx, getFormikData }) => {
        const fieldData = getFormikData<string>({
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const selectedDate = new Date(Number(e.target.value));
            const endDate = new Date(Number(ctx.values.ScheduleEndDate));
            if (isBefore(selectedDate, endDate)) {
              const newDate = addDays(selectedDate, 1).getTime();
              ctx.setFieldValue('ScheduleEndDate', newDate.toString());
            }
          },
        });
        return (
          <DatepickerSimple
            variant="outlineShadow"
            min={addDays(new Date(), 1).getTime() as Timestamp}
            iconPosition="left"
            {...fieldData}
            isDisabled={fieldData.disabled}
          />
        );
      },
      validate: () =>
        Yup.string()
          .required()
          .test(
            'start-date-validation',
            t('RemainderPaymentDate.invalidDateValidation', { now }),
            (value: string | undefined) => {
              if (!value) return false;
              const selectedDate = new Date(Number(value));
              return !isBefore(selectedDate, addDays(new Date(), 1));
            },
          ),
    },
  ];
}

export function useElinxInstallmentPlansRows(formType: ElinxType, isEnabled: boolean): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.InstallmentPlan' });

  const installmentFrequencyOptions: SelectOption<BasicOptions>[] = [
    { label: t('InstallmentFrequency.Options.weekly'), value: 'weekly' },
    { label: t('InstallmentFrequency.Options.biweekly'), value: 'biweekly' },
    { label: t('InstallmentFrequency.Options.monthly'), value: 'monthly' },
  ];
  return [
    {
      label: () => t('NumberOfInstallments.label'),
      fieldName: 'NumberOfInstallments',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments'),
      serializeField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments')
          ? sanitizeToNumeric(values.NumberOfInstallments)
          : undefined,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="numericWholePositive"
            placeholder={t('NumberOfInstallments.placeholder')}
            {...fieldData}
          />
        );
      },
      tooltip: () => {
        return <Tooltip information={t('NumberOfInstallments.tooltip')} position="top" width="394px" />;
      },
      validate: () =>
        Yup.number()
          .transform((value) => Number(value) || 0)
          .min(1, t('NumberOfInstallments.atLeastOneValidation'))
          .required(),
    },
    {
      label: () => t('InstallmentFrequency.label'),
      fieldName: 'InstallmentFrequency',
      showField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments'),
      getInputState: () => 'required',
      serializeField: (values: ElinxInfo) =>
        isEnabled && formType === 'Flex' && !!values.FlexiblePaymentOptions?.includes('installments')
          ? values.InstallmentFrequency
          : undefined,
      tooltip: () => {
        return <Tooltip information={t('InstallmentFrequency.tooltip')} position="top" width="394px" />;
      },
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('InstallmentFrequency.placeholder')}
            options={installmentFrequencyOptions}
            {...fieldData}
          />
        );
      },

      validate: () => Yup.string().required(),
    },
  ];
}

export function useElinxDetailsRows(formType: ElinxType): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ELinxDetails' });
  return [
    {
      label: () => t('SenderName.label'),
      fieldName: 'SenderName',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.SenderName,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('SenderName.placeholder')} {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('RecipientMessage.label'),
      fieldName: 'RecipientMessage',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.RecipientMessage,
      tooltip: () => <Tooltip information={t('RecipientMessage.tooltip')} position="top" width="394px" />,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder={t('RecipientMessage.placeholder')} {...fieldData} />;
      },
    },
    {
      label: () => t('InvoiceNumber.label'),
      fieldName: 'InvoiceNumber',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.InvoiceNumber,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
    },
    {
      label: () => t('UploadInvoice.label'),
      fieldName: 'InvoiceFile',
      getInputState: () => 'optional',
      showField: () => formType !== 'Connect',
      renderField: ({ getFormikData, ctx }) => {
        const fieldData = getFormikData<string>({});
        return (
          <UploadFile
            id={fieldData.name}
            onChangeFileName={(e: React.ChangeEvent<HTMLInputElement>) =>
              ctx.setFieldValue('InvoiceName', e.target.value)
            }
            valueFileName={ctx.values.InvoiceName}
            maxMBSize={4}
            validExtensions={['jpeg', 'png', 'pdf']}
            base64
            {...fieldData}
          />
        );
      },
    },
  ];
}

export function useElinxSettingsRows(
  isReview: boolean,
  defaultNotificationMethod: ElinxNotificationMethod,
  selectedContact: Contact | null,
  country: string,
): FieldData<ElinxInfo>[] {
  const { t } = useTranslation('translations', { keyPrefix: 'Elinx.Form.ELinxSettings' });
  const dialog = useDialog();
  const [newEmails, setNewEmails] = useState<string[]>([]);

  return [
    {
      label: () => t('SendNotification.label'),
      fieldName: 'SendNotification',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.SendNotification,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});
        return (
          <CardCheckbox
            indicator="switch"
            checked={fieldData.value}
            label={t('SendNotification.placeholder')}
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('SendReminder.label'),
      fieldName: 'SendReminder',
      getInputState: () => 'optional',
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.SendReminder,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<boolean>({});
        return (
          <CardCheckbox
            indicator="switch"
            checked={fieldData.value}
            label={t('SendReminder.checkboxLabel')}
            {...fieldData}
          />
        );
      },
    },
    {
      label: () => t('ReminderFrequency.label'),
      fieldName: 'ReminderFrequency',
      getInputState: (ctx) => (ctx.values.SendReminder ? 'required' : 'disabled'),
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.ReminderFrequency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: (ctx) => (ctx.values.ReminderFrequency ? Yup.string().required() : Yup.string()),
    },
    {
      label: () => t('NotificationMethod.label'),
      fieldName: 'NotificationMethod',
      getInputState: (ctx) => (ctx.values.SendNotification ? 'optional' : 'disabled'),
      showField: () => true,
      serializeField: (values: ElinxInfo) => values.NotificationMethod,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        const options = {
          email: t('NotificationMethod.Options.email'),
          sms: t('NotificationMethod.Options.sms'),
          ...(defaultNotificationMethod === 'whatsapp' && { whatsapp: t('NotificationMethod.Options.whatsapp') }),
        };
        return (
          <Radio variant="outlineShadow" readOnly={isReview || fieldData.disabled} options={options} {...fieldData} />
        );
      },
    },
    {
      label: () => t('ReceiverPhoneNumber.label'),
      fieldName: 'ReceiverPhoneNumber',
      getInputState: () => 'required',
      showField: (values: ElinxInfo) =>
        !values.PaymentMethodDisplayOptions?.includes('Existing') &&
        values.NotificationMethod === 'sms' &&
        !!values.SendNotification &&
        !(!!values.PaymentSelectionMethod && values.PaymentSelectionMethod.includes('bank') && country === 'US'),
      serializeField: (values: ElinxInfo) => values.ReceiverPhoneNumber,
      tooltip: () => <Tooltip information={t('ReceiverPhoneNumber.tooltip')} position="top" width="298px" />,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.string().required(),
    },
    {
      label: () => t('AdditionalRecipientEmails.label', { defaultValue: 'Additional Recipients Email' }),
      fieldName: 'AdditionalRecipientEmails',
      getInputState: () => 'optional',
      showField: (ctx) => !ctx.PaymentMethodDisplayOptions?.includes('Existing'),
      tooltip: () => (
        <Tooltip
          information={t('AdditionalRecipientEmails.tooltip', {
            defaultValue:
              'This is an additional email address. Any address added here will also receive all notifications and reminders.',
          })}
          position="top"
          width="298px"
        />
      ),
      renderField: ({ getFormikData, ctx }) => {
        const savedAdditionalEmails = selectedContact?.additionalContactEmails || [];
        const newAdditionalEmails = Array.from(new Set([...savedAdditionalEmails, ...newEmails]));
        const recipientEmails: string[] = Array.from(
          new Set([
            ...savedAdditionalEmails,
            ...ctx.values.AdditionalRecipientEmails,
            ...(ctx.values.OverrideEmailAddress ? [ctx.values.OverrideEmailAddress] : []),
          ]),
        );

        // do not show OverrideEmailAddress in Additional Recipients, but still check for duplicates.
        const visibleEmails = recipientEmails.filter(
          (email) => email.trim() && email !== ctx.values.OverrideEmailAddress,
        );

        const canAddEmail = (email: string) => {
          if (!email) return false;
          if (!Yup.string().email().required().isValidSync(email)) return false;
          if (recipientEmails.includes(email)) return false;
          return true;
        };

        const options = newAdditionalEmails.map((email) => ({
          value: email,
          label: email,
        }));

        const multiSelectFieldData = getFormikData<string[]>({
          formatValue: (value) => (Array.isArray(value) ? value : []),
          onChange: (e) => {
            const value = e.target.value;
            ctx.setFieldValue('AdditionalRecipientEmails', value === '' ? [] : value.split(','));
          },
        });

        async function handleAddEmail() {
          const newEmail = await dialog.open<string>(({ onConfirm, onCancel }) => (
            <AddEmailDialog emails={newAdditionalEmails} onClose={onCancel} onAdd={onConfirm} />
          ));

          if (newEmail) {
            setNewEmails((prev) => [...prev, newEmail]);
            ctx.setFieldValue('AdditionalRecipientEmails', [...ctx.values.AdditionalRecipientEmails, newEmail]);
          }
        }

        if (savedAdditionalEmails.length === 0)
          return (
            <EmailField
              emails={visibleEmails}
              onAdd={(newEmail) => {
                if (!canAddEmail(newEmail)) return;
                ctx.setFieldValue('AdditionalRecipientEmails', [...ctx.values.AdditionalRecipientEmails, newEmail]);
              }}
              onRemove={(email) => {
                ctx.setFieldValue(
                  'AdditionalRecipientEmails',
                  ctx.values.AdditionalRecipientEmails.filter((m) => m !== email),
                );
              }}
              {...getFormikData<string>({})}
            />
          );

        return (
          <>
            <MultiSelect
              placeholder={t('AdditionalRecipientEmails.selectPlaceholder', { defaultValue: 'Select Email Address' })}
              renderSelectedValue={(_, { Placeholder }) => <Placeholder />}
              options={options}
              renderOption={(o, { Option, SelectedIcon, selected }) => (
                <Option>
                  {o.label}
                  {selected && <SelectedIcon />}
                </Option>
              )}
              renderDropdownFooter={({ close }) => {
                return (
                  <Button
                    iconLeft="add"
                    size="small"
                    className={classes('addEmailButton')}
                    onClick={() => {
                      close();
                      handleAddEmail();
                    }}
                  >
                    {t('AdditionalRecipientEmails.addButton', { defaultValue: 'Additional Email Address' })}
                  </Button>
                );
              }}
              {...multiSelectFieldData}
            />
            {!!ctx.values.AdditionalRecipientEmails.length && (
              <div className={classes('multiselectOptionsContainer')}>
                {ctx.values.AdditionalRecipientEmails.map((email) => (
                  <div key={email} className={classes('multiselectOption')}>
                    <span>{email}</span>
                    <div
                      className={classes('item', isReview && 'disabled')}
                      onClick={() => {
                        const filteredList = ctx.values.AdditionalRecipientEmails.filter((e) => {
                          return email !== e;
                        });
                        ctx.setFieldValue('AdditionalRecipientEmails', filteredList);
                      }}
                    >
                      <MaterialSymbol icon="close" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      },
      validate: () =>
        Yup.array().test(
          'is-valid-emails',
          t('AdditionalRecipientEmails.validation', {
            defaultValue: 'Please enter a valid comma-separated list of email(s).',
          }),
          (value?: string[]) => {
            if (!value || value.length === 0) return true;
            return value.every((email) => Yup.string().email().isValidSync(email.trim()));
          },
        ),
    },
  ];
}

function isToday(dateStr: string): boolean {
  const date = startOfDay(new Date(Number(dateStr)));
  const today = startOfDay(new Date());
  return isEqual(date, today);
}
export const mapToPaymentSelectionValue = (paymentMethod: PaymentMethodType): string => {
  const mapping: Record<PaymentMethodType, string> = {
    BankAccount: 'bank',
    CreditCard: 'credit',
    DebitCard: 'debitcard',
    PhoneNumber: 'email',
    Email: 'email',
    GooglePay: 'googlepay',
    ApplePay: 'applepay',
    PayPal: 'paypal',
    Venmo: 'venmo',
    Zelle: 'zelle',
  };

  return mapping[paymentMethod] || '';
};

// Helper function to compute default PaymentSelectionMethod values
export const getDefaultPaymentSelectionMethods = (
  formType: 'Pay' | 'Collect' | 'Connect' | 'Flex',
  country: string,
  lockedSelectOptions: Record<string, string> | undefined,
  scheduleStartDate?: string,
  frequency?: string,
  paymentMethods?: PaymentMethod[],
  paymentMethodDisplayOptions?: string,
): string => {
  type PaymentOption = {
    value: string;
    featureKey: string;
    visible: boolean;
  };

  const options: PaymentOption[] = [
    {
      value: 'bank',
      featureKey: 'Bank Account',
      visible: true,
    },
    {
      value: 'email',
      featureKey: formType === 'Pay' ? 'Interac e-Transfer' : 'Interac e-Transfer Money Request',
      visible: country === 'CA' && formType !== 'Flex' && formType !== 'Connect',
    },
    {
      value: 'email',
      featureKey: 'Email',
      visible: country === 'CA' && formType === 'Connect',
    },
    {
      value: 'credit',
      featureKey: 'Credit Card',
      visible: formType !== 'Pay',
    },
    {
      value: 'debitcard',
      featureKey: 'Debit Card',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      value: 'googlePay',
      featureKey: 'Google Pay',
      visible: formType === 'Collect' || formType === 'Flex',
    },
    {
      value: 'paypal',
      featureKey: 'PayPal',
      visible: formType === 'Pay' || formType === 'Connect',
    },
    {
      value: 'venmo',
      featureKey: 'Venmo',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
    {
      value: 'zelle',
      featureKey: 'Zelle',
      visible: (formType === 'Pay' || formType === 'Connect') && country === 'US',
    },
  ];

  const isTodaySchedule = scheduleStartDate ? isToday(scheduleStartDate) : true;
  const isSingleFrequency = frequency === 'single' || !frequency;

  // Filter based on visibility, locked status, and scheduling constraints
  let availableOptions = options.filter((option) => {
    if (!option.visible) return false;
    // Check if locked via LockedSelectOptions
    if (lockedSelectOptions && option.featureKey in lockedSelectOptions) return false;
    // If not today or not single frequency, only allow bank
    if (!(isTodaySchedule && isSingleFrequency) && option.value !== 'bank') return false;
    return true;
  });

  // If PaymentMethodDisplayOptions is "Existing", filter by available payment methods
  if (paymentMethodDisplayOptions === 'Existing' && paymentMethods) {
    const existingPaymentMethodValues = new Set(
      paymentMethods.map((pm) => mapToPaymentSelectionValue(pm.PaymentMethod)),
    );
    availableOptions = availableOptions.filter((option) => existingPaymentMethodValues.has(option.value));
  }

  // Remove duplicates and join
  const uniqueValues = [...new Set(availableOptions.map((opt) => opt.value))];
  return uniqueValues.join(',');
};

export const getDefaultBankingPaymentRails = (
  formType: 'Pay' | 'Collect' | 'Connect' | 'Flex',
  country: string,
  lockedSelectOptions: Record<string, string> | undefined,
  scheduleStartDate?: string,
  frequency?: string,
): string => {
  type BankingRailOption = {
    value: string;
    featureKey: string;
    visible: boolean;
    isRecurringEnabled: boolean;
  };

  const options: BankingRailOption[] = [
    {
      value: 'eft',
      featureKey: 'eft',
      visible: country === 'CA',
      isRecurringEnabled: true,
    },
    {
      value: 'vopayinstant',
      featureKey: 'VoPay Instant',
      visible: country === 'CA' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      value: 'ach',
      featureKey: 'USD',
      visible: country === 'US',
      isRecurringEnabled: true,
    },
    {
      value: 'rtp',
      featureKey: 'RTP',
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
    {
      value: 'fedwire',
      featureKey: 'Fedwire',
      visible: country === 'US' && formType === 'Pay',
      isRecurringEnabled: false,
    },
  ];

  const isTodaySchedule = scheduleStartDate ? isToday(scheduleStartDate) : true;
  const isSingleFrequency = frequency === 'single' || !frequency;

  const availableOptions = options.filter((option) => {
    if (!option.visible) return false;
    // Check if locked via LockedSelectOptions
    if (lockedSelectOptions && option.featureKey in lockedSelectOptions) return false;
    // If not today or not single frequency, only allow recurring-enabled rails
    if (!(isTodaySchedule && isSingleFrequency) && !option.isRecurringEnabled) return false;
    return true;
  });

  return availableOptions.map((opt) => opt.value).join(',');
};
